import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Grid,
  IconButton,
  Avatar,
  Divider,
} from "@mui/material";
import {
  RecordVoiceOver as RecordVoiceOverIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Grade as GradeIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  PictureAsPdf as PdfIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  LocalHospital as LocalHospitalIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Brand Colors
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   OET Grade Helper
--------------------------------------------------------------- */
function getOetGrade(marks) {
  if (marks == null) return null;
  const score = Number(marks);
  if (score >= 450) return { grade: "A", color: SUCCESS_GREEN };
  if (score >= 400) return { grade: "B", color: "#2196f3" };
  if (score >= 350) return { grade: "C+", color: "#ff9800" };
  if (score >= 300) return { grade: "C", color: "#ff9800" };
  if (score >= 200) return { grade: "D", color: BRAND_PRIMARY };
  return { grade: "E", color: BRAND_DARK };
}

/* ---------------------------------------------------------------
   Audio Player Component
--------------------------------------------------------------- */
function AudioPlayer({ audioUrl, audioName, index }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "#fff",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Play/Pause Button */}
        <IconButton
          onClick={handlePlayPause}
          sx={{
            width: 48,
            height: 48,
            bgcolor: BRAND_PRIMARY,
            color: "#fff",
            "&:hover": { bgcolor: BRAND_DARK },
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        {/* Audio Info & Progress */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <MusicNoteIcon sx={{ fontSize: 16, color: BRAND_PRIMARY }} />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#1e293b" }}
              noWrap
            >
              Part {index + 1} Recording
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "#64748b", display: "block", mb: 1 }}
            noWrap
          >
            {audioName || "Audio Recording"}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                height: 4,
                bgcolor: "#e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${progressPercent}%`,
                  height: "100%",
                  bgcolor: BRAND_PRIMARY,
                  transition: "width 0.1s",
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b", minWidth: 70 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        style={{ display: "none" }}
      />
    </Card>
  );
}

/* ---------------------------------------------------------------
   Speaking Card Display (Roleplayer/Candidate Cards)
--------------------------------------------------------------- */
function SpeakingCardDisplay({ card, index }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        height: "100%",
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: BRAND_BG,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Chip
            label={card.cardType || `Card ${card.cardNumber}`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: BRAND_PRIMARY,
              color: BRAND_PRIMARY,
              fontWeight: 600,
            }}
          />
          <HtmlRenderer
            content={card.roleLabel}
            component="span"
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: BRAND_PRIMARY,
            }}
          />
        </Box>
      </Box>

      <CardContent sx={{ p: 2.5 }}>
        {/* Setting */}
        {card.setting && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Setting:
            </Typography>
            <HtmlRenderer
              content={card.setting}
              component="div"
              sx={{ fontWeight: 500, color: "#1e293b" }}
            />
          </Box>
        )}

        {/* Description */}
        {card.description && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Description:
            </Typography>
            <HtmlRenderer
              content={card.description}
              component="div"
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.7,
                color: "#374151",
              }}
            />
          </Box>
        )}

        {/* Tasks */}
        {card.tasks && (
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 600, display: "block", mb: 0.5 }}
            >
              Tasks:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#f8fafc",
                borderRadius: 1,
              }}
            >
              <HtmlRenderer
                content={card.tasks}
                component="div"
                sx={{
                  whiteSpace: "pre-line",
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                  color: "#374151",
                }}
              />
            </Paper>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------
   Main Card Section (Contains Roleplayer & Candidate Cards)
--------------------------------------------------------------- */
function MainCardSection({ mainCard, index, audios }) {
  const cards = mainCard.cards || [];
  // Find the audio for this main card (if any)
  const audioForCard = audios[index];

  return (
    <Box sx={{ mb: 4 }}>
      {/* Main Card Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          pb: 1,
          borderBottom: `2px solid ${BRAND_PRIMARY}`,
        }}
      >
        <Avatar
          sx={{
            bgcolor: BRAND_PRIMARY,
            width: 36,
            height: 36,
          }}
        >
          {mainCard.mainCardNumber || index + 1}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
          Role-Play {mainCard.mainCardNumber || index + 1}
        </Typography>
      </Box>

      {/* Cards Grid (Roleplayer & Candidate) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {cards.map((card, cardIdx) => (
          <Grid item xs={12} md={6} key={card._id || cardIdx}>
            <SpeakingCardDisplay card={card} index={cardIdx} />
          </Grid>
        ))}
      </Grid>

      {/* Audio for this Main Card */}
      {audioForCard && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "#64748b", mb: 1, fontWeight: 600 }}
          >
            Your Recording for Role-Play {index + 1}:
          </Typography>
          <AudioPlayer
            audioUrl={audioForCard.audioUrl}
            audioName={audioForCard.originalName}
            index={index}
          />
        </Box>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------
   Evaluation Section
--------------------------------------------------------------- */
function EvaluationSection({
  isEvaluated,
  marks,
  grade,
  adminComments,
  evaluationPdf,
  checkingStatus,
  evaluatorName,
}) {
  const gradeInfo = marks !== null && marks !== undefined ? getOetGrade(marks) : null;

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
        borderRadius: 2,
        mt: 3,
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
          borderBottom: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isEvaluated ? (
            <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
          ) : (
            <ScheduleIcon sx={{ color: "#64748b" }} />
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isEvaluated ? SUCCESS_GREEN : "#64748b",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {isEvaluated ? "Assessor Evaluation" : "Pending Evaluation"}
          </Typography>
        </Box>

        {isEvaluated && marks !== null && marks !== undefined && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: gradeInfo?.color || BRAND_PRIMARY }}
            >
              {marks}
            </Typography>
            <Chip
              icon={<GradeIcon sx={{ fontSize: 16 }} />}
              label={`Grade ${grade || gradeInfo?.grade}`}
              sx={{
                bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                color: gradeInfo?.color || BRAND_PRIMARY,
                fontWeight: 600,
              }}
            />
          </Box>
        )}
      </Box>

      <CardContent>
        {isEvaluated ? (
          <Box>
            {/* Evaluator Info */}
            {evaluatorName && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Evaluated by: <strong>{evaluatorName}</strong>
                </Typography>
              </Box>
            )}

            {/* Comments */}
            {adminComments && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Examiner's Comments
                  </Typography>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    borderLeft: `3px solid ${BRAND_PRIMARY}`,
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.7,
                      color: "#374151",
                    }}
                  >
                    {adminComments}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* PDF Download */}
            {evaluationPdf && (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <PdfIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Detailed Evaluation Report
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  href={evaluationPdf}
                  target="_blank"
                  sx={{
                    bgcolor: BRAND_PRIMARY,
                    "&:hover": { bgcolor: BRAND_DARK },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                  }}
                >
                  Download Evaluation PDF
                </Button>
              </Box>
            )}

            {!adminComments && !evaluationPdf && (
              <Typography sx={{ color: "#64748b" }}>
                Your speaking has been evaluated. Score and grade are shown above.
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                bgcolor: BRAND_BG,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <ScheduleIcon sx={{ fontSize: 32, color: BRAND_LIGHT }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 1 }}>
              Evaluation in Progress
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
              Your speaking will be evaluated within 24 hours. Please check back later
              for your results.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------
   Main SpeakingSectionReview Component
--------------------------------------------------------------- */
export default function SpeakingSectionReview({
  speakingData = {},
  submissionData = {},
}) {
  // Extract test data
  const test = speakingData.test || speakingData;
  const mainCards = test.mainCards || [];
  const testTitle = test.title || "";
  const discipline = test.discipline || "";

  // Submission data
  const speakingAudios = submissionData.speakingAudios || [];
  const evaluationPdf = submissionData.accessorFeedBackInPdf?.pdfUrl || "";
  const marks = submissionData.score ?? submissionData.marks;
  const grade = submissionData.grade || "";
  const adminComments = submissionData.accessorFeedBack || "";
  const checkingStatus = submissionData.checkingStatus || "";
  const evaluatorName = submissionData.evaluatorId?.fullName || "";
  const isEvaluated = (marks !== null && marks !== undefined) || !!evaluationPdf;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 0,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left - Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: BRAND_PRIMARY,
                width: 40,
                height: 40,
              }}
            >
              <RecordVoiceOverIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Speaking Module Review
              </Typography>
              <Typography
                sx={{ color: "#64748b", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {testTitle || `${mainCards.length} Role-Play${mainCards.length !== 1 ? "s" : ""}`}
                {discipline && ` • ${discipline.charAt(0).toUpperCase() + discipline.slice(1)}`}
              </Typography>
            </Box>
          </Box>

          {/* Right - Status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={
                isEvaluated ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ScheduleIcon sx={{ fontSize: 16 }} />
                )
              }
              label={checkingStatus === "pending" ? "Pending Evaluation" : isEvaluated ? "Evaluated" : "Pending"}
              sx={{
                bgcolor: isEvaluated ? SUCCESS_BG : BRAND_BG,
                color: isEvaluated ? SUCCESS_GREEN : BRAND_PRIMARY,
                fontWeight: 600,
              }}
            />
            {isEvaluated && marks !== null && marks !== undefined && (
              <Chip
                label={`Score: ${marks}`}
                sx={{
                  bgcolor: `${getOetGrade(marks)?.color || BRAND_PRIMARY}20`,
                  color: getOetGrade(marks)?.color || BRAND_PRIMARY,
                  fontWeight: 700,
                }}
              />
            )}
            {evaluatorName && (
              <Chip
                label={`Evaluator: ${evaluatorName}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#e2e8f0" }}
              />
            )}
          </Box>
        </Box>
      </Card>

      {/* Content */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Audio Submissions Section */}
        {speakingAudios.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <VolumeUpIcon sx={{ color: BRAND_PRIMARY }} />
              Your Audio Submissions ({speakingAudios.length})
            </Typography>
            <Grid container spacing={2}>
              {speakingAudios.map((audio, idx) => (
                <Grid item xs={12} md={6} key={audio._id || idx}>
                  <AudioPlayer
                    audioUrl={audio.audioUrl}
                    audioName={audio.originalName}
                    index={idx}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Speaking Cards (Role-Plays) */}
        {mainCards.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <AssignmentIcon sx={{ color: BRAND_PRIMARY }} />
              Role-Play Cards
            </Typography>
            {mainCards.map((mainCard, idx) => (
              <MainCardSection
                key={mainCard._id || idx}
                mainCard={mainCard}
                index={idx}
                audios={speakingAudios}
              />
            ))}
          </Box>
        )}

        {/* Evaluation Section */}
        <EvaluationSection
          isEvaluated={isEvaluated}
          marks={marks}
          grade={grade}
          adminComments={adminComments}
          evaluationPdf={evaluationPdf}
          checkingStatus={checkingStatus}
          evaluatorName={evaluatorName}
        />
      </Box>
    </Box>
  );
}
